import { CssBaseline, ThemeProvider } from "@mui/material";

import type { NextPage } from "next";
import { appWithTranslation } from "next-i18next";
import { DefaultSeo } from "next-seo";
import { withTranslateRoutes } from "next-translate-routes";

import { AppProps } from "next/app";
import Head from "next/head";

import type { ReactElement, ReactNode } from "react";
import React from "react";

import { CacheProvider, EmotionCache } from "@emotion/react";

import "typeface-quicksand";

import { AnalyticsWrapper } from "../features/analytics";
import SEO from "../lib/seo/config";
import theme from "../theme/primary";
import createEmotionCache from "../utils/createEmotionCache";

export type NextPageWithLayout<P = Record<string, unknown>, IP = P> = NextPage<P, IP> & {
    getLayout?: (page: ReactElement) => ReactNode;
};

type AppPropsWithLayout = AppProps & {
    Component: NextPageWithLayout;
};

// Client-side cache, shared for the whole session of the user in the browser.
const clientSideEmotionCache = createEmotionCache();

type MyAppProps = AppPropsWithLayout & {
    emotionCache?: EmotionCache;
};

// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
const defaultProps: MyAppProps = {
    emotionCache: undefined
};

const MyApp = (props: MyAppProps) => {
    const { Component, emotionCache = clientSideEmotionCache, pageProps } = props;

    // Use the layout defined at the page level, if available
    const getLayout = Component.getLayout ?? ((page) => page);

    return (
        <CacheProvider value={emotionCache}>
            {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
            {/* @ts-ignore */}
            {/* <IntlProvider locale={locale} messages={messages[locale]}> */}
            <Head>
                {/* todo: Other favicon sizes */}
                {/* <link rel="shortcut icon" href="/favicons/favicon.ico" /> */}
                {/* <link rel="apple-touch-icon" sizes="180x180" href="/favicons/apple-touch-icon.png" /> */}
                <link
                    rel="icon"
                    type="image/png"
                    sizes="32x32"
                    href="/favicons/favicon-32x32.png"
                />
                {/* <link rel="icon" type="image/png" sizes="16x16" href="/favicons/favicon-16x16.png" /> */}

                <meta name="viewport" content="initial-scale=1, width=device-width" />
            </Head>
            {/* eslint-disable-next-line react/jsx-props-no-spreading */}
            <DefaultSeo {...SEO} />
            <ThemeProvider theme={theme}>
                {/* CssBaseline kickstart an elegant, consistent, and simple baseline to build upon. */}
                <CssBaseline />
                {/* eslint-disable-next-line react/jsx-props-no-spreading */}
                {getLayout(<Component {...pageProps} />)}
            </ThemeProvider>

            <AnalyticsWrapper />
        </CacheProvider>
    );
};

MyApp.defaultProps = defaultProps;

export default withTranslateRoutes(JSON.parse(`{"defaultLocale":"en","locales":["en","fr"],"routesTree":{"name":"","paths":{"default":""},"children":[{"name":"about-site","paths":{"default":"about-site","fr":"historique-site"}},{"name":"about","paths":{"default":"about","fr":"a-propos"}},{"name":"experience","paths":{"default":"experience","fr":"experience-competences"}},{"name":"index","paths":{"default":"index"}},{"name":"projects","paths":{"default":"projects","fr":"projets"}},{"name":"rss-fr.xml","paths":{"default":"rss-fr.xml"}},{"name":"rss.xml","paths":{"default":"rss.xml"}},{"name":"search","paths":{"default":"search","fr":"rechercher"}},{"name":"sitemap.xml","paths":{"default":"sitemap.xml"}},{"name":"stats","paths":{"default":"stats","fr":"stats"}},{"name":"articles","paths":{"default":"articles"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[slug]","paths":{"default":":slug"}},{"name":"page","paths":{"default":"page"},"children":[{"name":"[id]","paths":{"default":":id"}}]}]},{"name":"contact","paths":{"default":"contact","fr":"contacter"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"thanks","paths":{"default":"thanks","fr":"merci"}}]},{"name":"tags","paths":{"default":"tags","note":"Tags is the same in FR and EN, so noty translations here yet!"},"children":[{"name":"index","paths":{"default":"index"}},{"name":"[slug]","paths":{"default":":slug"}}]},{"name":"theme","paths":{"default":"theme"},"children":[{"name":"index","paths":{"default":"index"}}]}]},"fallbackLng":{}}`), appWithTranslation(MyApp));
